import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DIALOG_DATA, DialogRef, DialogService, ForgeBadgeModule, ForgeButtonModule, ForgeCardModule, ForgeDialogModule, ForgeDividerModule, ForgeExpansionPanelModule, ForgeIconButtonModule, ForgeIconModule, ForgeLabelValueModule, ForgeOpenIconModule, ForgeScaffoldModule, ForgeTabBarModule, ForgeToolbarModule, ForgeTooltipModule, ForgeViewSwitcherModule, IDialogOptions } from '@tylertech/forge-angular';
import { Subscription } from 'rxjs';
import { SharedModule } from 'src/app/modules/shared/shared.module';

import { DocInmate } from 'src/app/models/doc-inmate.model';
import { InmateStatusClassPipe } from 'src/app/utils/_pipes/inmate-status-class.pipe';
import { getLocationDisplay } from 'src/app/utils/facility-names';
import { formatAliases } from 'src/app/utils/format-aliases';
import { ImageDetails, InmatesService } from '@tylertech/cj-inmate-search-angular-sdk';
import { RiskAssessmentPipe } from 'src/app/utils/_pipes/risk-assessment.pipe';
import { getEyeColorLabel, getGenderLabel, getHairColorLabel, getOffenderStatusLabel, getRaceLabel } from 'src/app/constants/doc-options.constants';
import { InstanceConfigService } from 'src/app/core/services/instance-config.service';
import { toMugshotDetails } from 'src/app/utils/image.utils';
import { EnlargedMugshotModalComponent } from 'src/app/components/enlarged-mugshot-modal/enlarged-mugshot-modal.component';

function normalizeDate(dateVal: any): string | null {
  if (!dateVal) return null;
  const str = String(dateVal).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    return `${str}T12:00:00Z`;
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
    return `${str.substring(0, 10)}T12:00:00Z`;
  }
  return str;
}

@Component({
  selector: 'doc-inmate-details',
  templateUrl: './doc-inmate-details.component.html',
  styleUrls: ['./doc-inmate-details.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    ForgeTabBarModule,
    ForgeViewSwitcherModule,
    ForgeToolbarModule,
    ForgeIconButtonModule,
    ForgeIconModule,
    ForgeBadgeModule,
    ForgeButtonModule,
    ForgeScaffoldModule,
    ForgeCardModule,
    ForgeDialogModule,
    ForgeExpansionPanelModule,
    ForgeLabelValueModule,
    ForgeOpenIconModule,
    ForgeDividerModule,
    ForgeTooltipModule,
    InmateStatusClassPipe,
    RiskAssessmentPipe
  ]
})
export class DocInmateDetailsComponent implements OnInit, OnDestroy {
  public inmate!: DocInmate;
  public activeTabIndex = 0;
  protected mugshotImageDetails: ImageDetails | null = null;
  public hideMugShots = false;
  private _vineUrl: string | null = null;
  private readonly _subscriptions: Subscription[] = [];

  constructor(
    @Inject(DIALOG_DATA) public data: { inmate: DocInmate },
    private dialogRef: DialogRef,
    private readonly _inmatesService: InmatesService,
    private readonly instanceConfigService: InstanceConfigService,
    private readonly _dialogService: DialogService
  ) {}

  ngOnInit(): void {
    this.inmate = { ...this.data.inmate };
    this.normalizeBasicInfo();
    this.normalizeOffenses();
    this.normalizeHearings();
    this.normalizeAdditionalInfo();

    const visibility = this.instanceConfigService?.inmateVisibilitySnapshot;
    if (visibility) {
      this.hideMugShots = visibility.hideMugShots ?? false;
    }

    if (!this.hideMugShots) {
      this.loadMugshot();
    }

    this._subscriptions.push(
      this.instanceConfigService.branding$.subscribe(branding => {
        const url = branding?.vineUrl;
        this._vineUrl = url?.trim() || null;
      })
    );
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(sub => sub.unsubscribe());
  }

  private normalizeBasicInfo(): void {
    this.inmate.pid = this.inmate.pid || this.inmate.docNumber;

    if (this.inmate.dateOfBirth) {
      this.inmate.dateOfBirth = normalizeDate(this.inmate.dateOfBirth) || this.inmate.dateOfBirth;
    }

    const supObj = typeof (this.inmate as any).supervisionStatus === 'object' ? (this.inmate as any).supervisionStatus : {};
    this.inmate.supervisionStart = normalizeDate(this.inmate.supervisionStart
      || supObj.supervisionStartDate
      || supObj.supervisionStart
      || supObj.supervision_start_date
      || (this.inmate as any).supervisionStartDate
      || (this.inmate as any).supervision_start) || undefined;

    this.inmate.supervisionEnd = normalizeDate(this.inmate.supervisionEnd
      || supObj.supervisionEndDate
      || supObj.supervisionEnd
      || supObj.supervision_end_date
      || (this.inmate as any).supervisionEndDate
      || (this.inmate as any).supervision_end) || undefined;

    this.inmate.custodyStartDate = normalizeDate(this.inmate.custodyStartDate || (this.inmate as any).custodyStart) || undefined;
    this.inmate.sentenceMaxDate = normalizeDate(this.inmate.sentenceMaxDate) || undefined;
    this.inmate.sentenceMinDate = normalizeDate(this.inmate.sentenceMinDate) || undefined;
    this.inmate.goodTimeReleaseDate = normalizeDate(this.inmate.goodTimeReleaseDate) || undefined;
    this.inmate.paroleEligibilityDate = normalizeDate(this.inmate.paroleEligibilityDate) || undefined;

    this.inmate.hairColor = this.inmate.hairColor || (this.inmate as any).hair;
    this.inmate.eyeColor = this.inmate.eyeColor || (this.inmate as any).eyes;
    if (this.inmate.weight && !String(this.inmate.weight).includes('lbs')) {
      this.inmate.weight = `${this.inmate.weight} lbs.`;
    }
  }

  private normalizeOffenses(): void {
    if (this.inmate.offenses && this.inmate.offenses.length > 0) {
      this.inmate.offenses = this.inmate.offenses.map((offense: any) => this.normalizeOffense(offense));
    }
  }

  private normalizeOffense(offense: any): any {
    const degree = offense.offenseTypeName || offense.degree || (offense.felonyClass ? 'Felony' : 'Unknown');
    return {
      ...offense,
      description: offense.description || offense.offenseDescription,
      krsCode: offense.krsCode || offense.statuteKRS,
      indictmentNumber: offense.indictmentNumber || offense.indictment,
      indictmentCount: offense.indictmentCount ?? offense.ICount ?? offense.count ?? offense.indictment_count,
      offenseTypeName: degree,
      degree: degree,
      sentenceLength: this.formatSentenceLength(offense),
      crimeDate: normalizeDate(offense.crimeDate || offense.crime_date),
      convictionDate: normalizeDate(offense.convictionDate || offense.conviction_date)
    };
  }

  private formatSentenceLength(offense: any): string | undefined {
    const { years, months, days } = this.extractSentenceParts(offense);
    const parts: string[] = [];

    if (this.isValidDuration(years)) parts.push(this.formatDuration(years, 'year', 'years'));
    if (this.isValidDuration(months)) parts.push(this.formatDuration(months, 'month', 'months'));
    if (this.isValidDuration(days)) parts.push(this.formatDuration(days, 'day', 'days'));

    return parts.length > 0 ? parts.join(', ') : offense.sentenceLength;
  }

  private extractSentenceParts(offense: any): { years?: any; months?: any; days?: any } {
    let years = offense.sentenceLengthYears ?? offense.years;
    let months = offense.sentenceLengthMonths ?? offense.months;
    let days = offense.sentenceLengthDays ?? offense.days;
    const text = offense.sentenceLength;

    if (years === undefined && months === undefined && days === undefined && typeof text === 'string') {
      years = /\b(\d+(?:\.\d+)?)\s*y(?:ear|r)s?\b/i.exec(text)?.[1];
      months = /\b(\d+(?:\.\d+)?)\s*m(?:on(?:th)?|o)?s?\b/i.exec(text)?.[1];
      days = /\b(\d+(?:\.\d+)?)\s*d(?:ay|a)?s?\b/i.exec(text)?.[1];
    }

    return { years, months, days };
  }

  private isValidDuration(value: any): boolean {
    return value !== undefined && value !== null && value !== '';
  }

  private formatDuration(val: any, singular: string, plural: string): string {
    const num = Number(val);
    const unit = (!isNaN(num) && num === 1) ? singular : plural;
    return `${val} ${unit}`;
  }

  private normalizeHearings(): void {
    const rawHearings = this.inmate.hearings || this.inmate.parole || (this.inmate as any).paroleHearings || [];
    if (rawHearings.length > 0) {
      this.inmate.hearings = rawHearings.map((p: any) => this.normalizeHearing(p));
    }
  }

  private normalizeHearing(p: any): any {
    const actionMap: { [key: string]: string } = {
      'D0': 'Deferred',
      'PR': 'Parole recommended',
      'SO': 'Serve out'
    };
    const rawAction = p.hearingAction || p.paroleAction || p.paroleActionCode || p.action;
    const action = actionMap[rawAction] || rawAction || 'N/A';

    const rawHDate = p.hearingDate || p.hearing_date || p.HearingDate || p.hearing || p.Date || p.date;
    const rawPDate = p.proposedReleaseDate || p.ProposedReleaseDate || p.paroleDate || p.parole_date || p.releaseDate || p.proposed_release_date;
    const rawPEDate = p.nextParoleEligibility || p.paroleEligibilityDate || p.PEDate || p.next_parole_eligibility || p.peDate;

    const hDate = normalizeDate(rawHDate || rawPEDate || rawPDate);
    const pDate = normalizeDate(rawPDate);
    const peDate = normalizeDate(rawPEDate);

    return {
      ...p,
      hearingDate: hDate,
      hearingType: p.hearingType || 'PAROLE',
      hearingAction: action,
      monthsDeferred: (p.monthsDeferred ?? p.months_deferred ?? p.MonthsDeferred ?? '').toString(),
      nextParoleEligibility: peDate,
      proposedReleaseDate: pDate
    };
  }

  private normalizeAdditionalInfo(): void {
    if (!this.inmate.paroleEligibilityDate && (this.inmate.parole?.length || this.inmate.hearings?.length)) {
      const allHearings = this.inmate.hearings || this.inmate.parole || [];
      const validParole = allHearings.find((p: any) => p.nextParoleEligibility || p.paroleEligibilityDate);
      if (validParole) {
        this.inmate.paroleEligibilityDate = normalizeDate(validParole.nextParoleEligibility || validParole.paroleEligibilityDate) || undefined;
      }
    }

    if (!this.inmate.height && this.inmate.heightFeet !== undefined && this.inmate.heightFeet !== null) {
      this.inmate.height = `${this.inmate.heightFeet} ft. ${this.inmate.heightInches || 0} in.`;
    }
  }

  onTabChange(event: CustomEvent): void {
    this.activeTabIndex = (event as CustomEvent<{ index: number }>).detail.index;
  }

  get calculatedAge(): number | string {
    if (this.inmate.age) return this.inmate.age;
    if (!this.inmate.dateOfBirth) return 'Unknown';
    const dob = new Date(this.inmate.dateOfBirth);
    const ageDifMs = Date.now() - dob.getTime();
    const ageDate = new Date(ageDifMs);
    return Math.abs(ageDate.getUTCFullYear() - 1970);
  }

  get locationDisplay(): string {
    return getLocationDisplay(this.inmate?.location);
  }

  get statusDisplay(): string {
    const rawStatus = this.inmate?.custodyStatus || this.inmate?.status;
    if (rawStatus) {
      return getOffenderStatusLabel(rawStatus);
    }
    const supObj = (this.inmate as any)?.supervisionStatus;
    let supName: string | undefined;
    if (typeof supObj === 'object' && supObj !== null) {
      supName = supObj?.supervisionStatusName || supObj?.supervisionStatus;
    } else if (typeof supObj === 'string') {
      supName = supObj;
    }
    return getOffenderStatusLabel(supName) || 'Unknown';
  }

  get raceLabel(): string {
    return this.inmate?.raceName?.trim() || getRaceLabel(this.inmate?.race);
  }

  get genderLabel(): string {
    return getGenderLabel(this.inmate?.gender);
  }

  get hairColorLabel(): string {
    return getHairColorLabel(this.inmate?.hairColor);
  }

  get eyeColorLabel(): string {
    return getEyeColorLabel(this.inmate?.eyeColor);
  }

  get formattedAliases(): string {
    return formatAliases(this.inmate?.aliases);
  }

  get riskAssessmentCode(): string | number | undefined {
    if (this.inmate?.riskAssessment != null && this.inmate.riskAssessment !== '') {
      return this.inmate.riskAssessment;
    }
    return this.inmate?.riskNeedLevelCode;
  }

  private loadMugshot(): void {
    if (!this.inmate?.instanceId || !this.inmate?.id || !this._inmatesService) return;
    this._inmatesService.getInmateImage(this.inmate.instanceId, this.inmate.id, (this.inmate as any).imageServiceClientId).subscribe({
      next: (imageDetails) => {
        this.mugshotImageDetails = toMugshotDetails(imageDetails);
      },
      error: (err) => {
        this.mugshotImageDetails = null;
        if (err.status === 404) return;
        console.error(err);
      }
    });
  }

  protected onImageError(): void {
    this.mugshotImageDetails = null;
  }

  openEnlargedMugshot(): void {
    if (this.hideMugShots || !this.mugshotImageDetails?.imageUrls) return;

    const imageUrl = this.mugshotImageDetails?.originalUrl
      ?? this.mugshotImageDetails?.imageUrls?.size800
      ?? this.mugshotImageDetails?.imageUrls?.size520
      ?? this.mugshotImageDetails?.imageUrls?.size320
      ?? 'assets/no-image-headshot.png';

    const title = this.inmate?.name?.fullName || 'Inmate mugshot';

    const options: IDialogOptions = {
      label: title,
      description: 'Enlarged inmate mugshot image',
      attributes: new Map([
        ['style', '--forge-dialog-width: 1024px; --forge-dialog-height: 768px; --forge-dialog-max-width: 90vw; --forge-dialog-max-height: 90vh;']
      ])
    };

    this._dialogService.open(EnlargedMugshotModalComponent, {
      options,
      data: {
        imageUrl,
        inmateName: title
      }
    });
  }
  closeDialog(): void {
    this.dialogRef.close();
  }

  get vineUrl(): string | null {
    if (this._vineUrl) return this._vineUrl;
    const url = this.instanceConfigService.brandingSnapshot?.vineUrl;
    return url?.trim() || null;
  }

  openVineUrl(): void {
    const baseUrl = this.vineUrl?.trim();
    if (!baseUrl) return;

    const offenderId = (this.inmate as any).offenderID || this.inmate.pid || this.inmate.docNumber || this.inmate.soNumber || this.inmate.id;
    if (!offenderId) return;

    try {
      const url = new URL(baseUrl);
      if (url.protocol !== 'http:' && url.protocol !== 'https:') return;

      url.searchParams.set('offenderID', String(offenderId));
      window.open(url.toString(), '_blank', 'noopener,noreferrer');
    } catch {
      // Invalid URL; ignore
    }
  }
}